/* eslint-disable max-len */
import React, {
  useRef,
  forwardRef,
  useEffect,
  useState,
  useCallback,
} from 'react'
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Sort,
  Inject,
  TextAlign,
  SortSettingsModel,
  SearchSettingsModel,
  ToolbarItems,
  SearchEventArgs,
  Toolbar,
  AggregatesDirective,
  AggregateDirective,
  AggregateColumnsDirective,
  AggregateColumnDirective,
  Aggregate,
  RowSelectEventArgs,
  SelectionSettingsModel,
  Group,
  GroupSettingsModel,
  ColumnModel,
  PageEventArgs,
  ActionEventArgs,
  DetailRow,
  RecordClickEventArgs,
  VirtualScroll,
} from '@syncfusion/ej2-react-grids'

import {
  RowDD,
  RowDropSettingsModel,
  RowDragEventArgs,
} from '@syncfusion/ej2-grids'
import { Predicate, Query } from '@syncfusion/ej2-data'
import PaginationContainer from '@components/common/PaginationContainer/PaginationContainer.component'
import './DataGridComponent.css'

type DataGridComponentProps = {
  data: any[]
  columns: (ColumnModel & { enableNumericSorting?: boolean })[]
  pageSize?: number
  pageCount?: number
  showCheckbox?: boolean
  showCheckboxWidth?: number | string
  customCSS?: string
  allowPaging?: boolean
  allowSorting?: boolean
  rowHeight?: number
  sortSettings?: SortSettingsModel
  searchOptions?: SearchSettingsModel
  hasFooter?: boolean
  footerData?: Array<{
    field: string
    template: (props: any) => JSX.Element
    textAlign?: TextAlign
  }>
  rowSelecting?: (args: any) => void
  rowSelected?: (args: RowSelectEventArgs) => void
  rowDeselected?: (args: RowSelectEventArgs) => void
  selectionSettings?: SelectionSettingsModel | undefined
  onRowDoubleClick?: (args: any) => void
  allowTextWrap?: boolean
  queryCellInfo?: any
  dataBound?: () => void
  rowDataBound?: (args: any) => void
  groupSettings?: GroupSettingsModel
  isLoading?: boolean
  pageChange?: (args: PageEventArgs) => void
  onActionComplete?: (args: ActionEventArgs) => void
  /** When allowPaging is true, show this custom pager below the grid. Optional page size options for the dropdown. */
  pageSizeOptions?: number[]
  allowSelection?: boolean
  detailTemplate?: (props: any) => JSX.Element
  onDetailDataBound?: (args: any) => void
  recordClick?: (args: RecordClickEventArgs) => void
  /** DOM id for the root grid element (e.g. cross-grid rowDropSettings.targetID). */
  gridDomId?: string
  /** When true, injects RowDD and forwards row drag/drop props to Syncfusion Grid. */
  allowRowDragAndDrop?: boolean
  rowDropSettings?: RowDropSettingsModel
  onRowDrop?: (args: RowDragEventArgs) => void
  onRowDragStart?: (args: any) => void
  /** When true, enables Syncfusion row virtualization (requires VirtualScroll). */
  enableVirtualization?: boolean
}

const DataGridComponent = forwardRef<
  GridComponent | null,
  DataGridComponentProps
>(
  (
    {
      data,
      columns,
      pageSize,
      pageCount,
      showCheckbox,
      showCheckboxWidth,
      customCSS,
      allowPaging = false,
      allowSorting,
      rowHeight,
      sortSettings,
      searchOptions,
      hasFooter,
      footerData,
      rowSelecting,
      rowSelected,
      rowDeselected,
      selectionSettings,
      onRowDoubleClick,
      allowTextWrap,
      queryCellInfo,
      dataBound,
      rowDataBound: customRowDataBound,
      groupSettings,
      isLoading,
      pageChange,
      onActionComplete,
      pageSizeOptions = [10, 25, 50, 100],
      allowSelection = true,
      detailTemplate,
      onDetailDataBound,
      recordClick,
      gridDomId,
      allowRowDragAndDrop = false,
      rowDropSettings,
      onRowDrop,
      onRowDragStart,
      enableVirtualization = false,
    },
    ref,
  ) => {
    const gridRef = useRef<GridComponent | null>(null)
    const safeData = Array.isArray(data) ? data : []

    const [currentPage, setCurrentPage] = useState<number>(1)
    const [currentPageSize, setCurrentPageSize] = useState<number>(
      pageSize ?? 10,
    )

    useEffect(() => {
      setCurrentPageSize(pageSize ?? 10)
    }, [pageSize])

    useEffect(() => {
      setCurrentPage(1)
      if (safeData.length > 0 && gridRef.current) {
        gridRef.current.goToPage(1)
      }
    }, [safeData.length])

    const handlePaginationContainerPageChange = useCallback(
      (args: PageEventArgs): void => {
        const page = parseInt(args.currentPage as string, 10) || 1
        gridRef.current?.goToPage(page)
      },
      [],
    )

    const handlePageSizeChange = useCallback((newPageSize: number): void => {
      setCurrentPageSize(newPageSize)
      setCurrentPage(1)
    }, [])

    useEffect(() => {
      if (!gridRef.current) return
      if (isLoading) {
        gridRef.current.showSpinner()
      } else {
        gridRef.current.hideSpinner()
      }
    }, [isLoading])

    const rowDataBound = (args: any): void => {
      // Call custom rowDataBound if provided
      if (customRowDataBound) {
        customRowDataBound(args)
      }

      // Built-in location-table specific logic
      if (customCSS !== 'location-table') return

      const isDisabled = args.data.disabled
      const color = isDisabled ? '#8a8e97' : '#212529'
      const backgroundColor = isDisabled ? '#eff1f3' : '#ffffff'
      const cells = args.row.querySelectorAll('td')

      cells.forEach((cell: any, index: number) => {
        const textAlign = index < 3 ? 'left' : 'center'
        cell.setAttribute(
          'style',
          `color: ${color}; background-color: ${backgroundColor} !important; text-align: ${textAlign};`,
        )
      })
    }

    const headerCellInfo = (args: any): void => {
      args.node.style.backgroundColor = '#eff1f3'
      args.node.style.color = '#4e525a'
      args.node.style.fontWeight = '600'
      args.node.style.fontSize = '0.875rem'
      args.node.style.lineHeight = '24px'
      args.node.style.height = '32px'
      const sortNumber = args.node.querySelector('.e-sortnumber')
      if (sortNumber) {
        sortNumber.style.display = 'none'
      }
    }

    const toolbarOptions: ToolbarItems[] = ['Search']

    const actionBegin = (args: SearchEventArgs): void => {
      if (args.requestType === 'searching' && searchOptions) {
        const searchString = args.searchString || ''
        const keys = searchString.split(',')

        let predicate: Predicate | null = null
        keys.forEach((key) => {
          gridRef.current?.getColumns().forEach((col) => {
            const newPredicate = new Predicate(col.field, 'contains', key, true)
            predicate = predicate ? predicate.or(newPredicate) : newPredicate
          })
        })

        if (predicate && gridRef.current) {
          gridRef.current.query = new Query().where(predicate)
          gridRef.current.refreshColumns()
        }
      }
    }

    const actionComplete = (args: SearchEventArgs): void => {
      const searchElement = document.getElementById(
        gridRef.current?.element.id + '_searchbar',
      ) as HTMLInputElement

      if (searchElement && searchOptions && args.requestType === 'refresh') {
        searchElement.value = searchOptions.key || ''
      }
    }

    const handleActionComplete = (args: ActionEventArgs): void => {
      if (searchOptions) {
        actionComplete(args as SearchEventArgs)
      }

      if (args.requestType === 'paging') {
        const pageArgs = args as unknown as PageEventArgs
        const nextPage =
          parseInt(pageArgs.currentPage as string, 10) ||
          gridRef.current?.pageSettings?.currentPage ||
          1
        setCurrentPage(nextPage)
        pageChange?.(pageArgs)
      }

      onActionComplete?.(args)
    }
    const selectionSettingsGrid: SelectionSettingsModel = showCheckbox
      ? { checkboxOnly: true }
      : { type: 'Single', mode: 'Row', enableToggle: false }

    const combinedRef = (node: GridComponent | null): void => {
      gridRef.current = node
      if (ref) {
        if (typeof ref === 'function') {
          ref(node)
        } else {
          ref.current = node
        }
      }
    }

    const handleDataBound = (): void => {
      if (typeof dataBound === 'function') {
        dataBound()
      }
      // EJ2 hides spinner automatically on dataBound; ensure it stays visible while loading
      if (isLoading && gridRef.current) {
        gridRef.current.showSpinner()
      }
    }

    return (
      <div
        className={`custom-datagrid-container ${allowPaging ? 'use-custom-pager' : ''} ${customCSS ?? ''} ${isLoading ? 'is-loading' : ''}`}
      >
        <GridComponent
          key={allowPaging ? `paged-${currentPageSize}` : 'no-paging'}
          {...(gridDomId ? { id: gridDomId } : {})}
          dataSource={safeData}
          rowDataBound={rowDataBound}
          enableVirtualization={enableVirtualization}
          allowPaging={allowPaging ?? true}
          allowSorting={allowSorting ?? true}
          pageSettings={{
            pageSize: currentPageSize,
            pageCount: pageCount,
            template: '<div class="e-grid-custom-pager-placeholder"></div>',
          }}
          sortSettings={sortSettings}
          searchSettings={searchOptions}
          headerCellInfo={headerCellInfo}
          rowHeight={rowHeight}
          toolbar={searchOptions ? toolbarOptions : undefined}
          actionBegin={searchOptions ? actionBegin : undefined}
          actionComplete={handleActionComplete}
          width={'100%'}
          allowResizing={true}
          selectionSettings={
            selectionSettings ? selectionSettings : selectionSettingsGrid
          }
          {...(rowSelecting ? { rowSelecting } : {})}
          rowSelected={rowSelected ?? undefined}
          rowDeselected={rowDeselected ?? undefined}
          {...(onRowDoubleClick ? { recordDoubleClick: onRowDoubleClick } : {})}
          allowTextWrap={allowTextWrap}
          queryCellInfo={queryCellInfo}
          dataBound={handleDataBound}
          ref={combinedRef}
          {...(groupSettings ? { allowGrouping: true } : {})}
          {...(groupSettings ? { groupSettings } : {})}
          {...(detailTemplate ? { detailTemplate } : {})}
          {...(onDetailDataBound ? { detailDataBound: onDetailDataBound } : {})}
          {...(recordClick ? { recordClick } : {})}
          allowSelection={allowSelection}
          {...(allowRowDragAndDrop ? { allowRowDragAndDrop: true } : {})}
          {...(rowDropSettings ? { rowDropSettings } : {})}
          {...(onRowDrop ? { rowDrop: onRowDrop } : {})}
          {...(onRowDragStart ? { rowDragStart: onRowDragStart } : {})}
        >
          <ColumnsDirective>
            {showCheckbox && (
              <ColumnDirective
                type="checkbox"
                width={showCheckboxWidth ?? 38}
                allowSorting={false}
              />
            )}
            {columns.map((col, index) => (
              <ColumnDirective
                {...col}
                key={index}
                textAlign={col.textAlign || 'Left'}
                width={col.width || '150'}
                allowSorting={col.allowSorting ?? true}
                {...(col.enableNumericSorting
                  ? {
                      sortComparer: (a: any, b: any) => {
                        const aNum = parseFloat(a) || 0
                        const bNum = parseFloat(b) || 0
                        return aNum - bNum
                      },
                    }
                  : {})}
              />
            ))}
          </ColumnsDirective>
          {hasFooter && (
            <AggregatesDirective>
              <AggregateDirective>
                <AggregateColumnsDirective>
                  {footerData?.map(({ field, template }) => (
                    <AggregateColumnDirective
                      key={`footer-${field}`}
                      field={field}
                      type={'Sum'}
                      footerTemplate={template}
                    />
                  ))}
                </AggregateColumnsDirective>
              </AggregateDirective>
            </AggregatesDirective>
          )}

          <Inject
            services={[
              Sort,
              Toolbar,
              Aggregate,
              Group,
              ...(allowPaging ? [Page] : []),
              ...(detailTemplate ? [DetailRow] : []),
              ...(allowRowDragAndDrop ? [RowDD] : []),
              ...(enableVirtualization ? [VirtualScroll] : []),
            ]}
          />
        </GridComponent>
        {allowPaging && safeData.length > 0 && (
          <PaginationContainer
            totalRecordsCount={safeData.length}
            pageSize={currentPageSize}
            currentPage={currentPage}
            pageSizeOptions={pageSizeOptions}
            onPageSizeChange={handlePageSizeChange}
            onPageChange={handlePaginationContainerPageChange}
          />
        )}
      </div>
    )
  },
)

DataGridComponent.displayName = 'DataGridComponent'

export default DataGridComponent
